import { ChevronRight } from "lucide-react";
import type { ReactNode } from "react";
import { Link } from "react-router";

/** Karte/Gruppe (Handoff Schiefer): weisse Flaeche (--pg-surface) auf --pg-bg, Radius 14,
 *  nur mit feiner Kante als Ring, ohne Schatten wie iOS (--pg-karte, kein border: Abnahme-Regel 1; W22d-w5), Titel darueber,
 *  optionale Fusszeile. Die Klasse `gruppe` dient der Pruefregel „keine Karte in einer Karte“. */
export function Gruppe(props: {
  titel?: ReactNode;
  /** W82-2 N3: Name des Titels fuer Vorlesen, wenn er vom sichtbaren Text abweicht (Anzahl sichtbar „1“, vorgelesen „(1)“). */
  titelLesetext?: string;
  /** Kurze Erklaerung unter dem Titel (W73b: „Abgelegte Briefe nach Kategorie“). */
  unterzeile?: ReactNode;
  fuss?: ReactNode;
  children: ReactNode;
  id?: string;
  aktion?: ReactNode;
  /** Liste per Skript fokussierbar (tabIndex -1, benannt nach dem Titel), etwa als Fokusziel. */
  listeFokussierbar?: boolean;
}) {
  return (
    <section className="mt-8 first:mt-4" aria-labelledby={props.titel ? props.id : undefined}>
      {(props.titel || props.aktion) && (
        // W82-2 N3: `gruppe-kopf` traegt unter data-welt den Abschnittskopf der Vorlage (welt.css); sonst ohne Wirkung
        <div className="gruppe-kopf mb-2 flex flex-wrap items-center justify-between gap-x-4 px-4">
          {props.titel && (
            <h2 id={props.id} className="t-title-3" aria-label={props.titelLesetext}>
              {props.titel}
            </h2>
          )}
          {props.aktion}
          {props.unterzeile && <p className="t-footnote basis-full text-pg-text-2">{props.unterzeile}</p>}
        </div>
      )}
      <ul
        id={props.listeFokussierbar && props.id ? `${props.id}-liste` : undefined}
        tabIndex={props.listeFokussierbar ? -1 : undefined}
        aria-labelledby={props.listeFokussierbar && props.titel ? props.id : undefined}
        className={`gruppe overflow-clip rounded-group bg-pg-surface shadow-karte ${props.listeFokussierbar ? "outline-none" : ""}`}
      >
        {props.children}
      </ul>
      {props.fuss && <p className="t-footnote mt-2 px-4 text-pg-text-2">{props.fuss}</p>}
    </section>
  );
}

type ZeilenProps = {
  children: ReactNode;
  vorne?: ReactNode;
  hinten?: ReactNode;
  zu?: string;
  onClick?: () => void;
  label?: string;
  chevron?: boolean;
  /** Einzug des Trenners: Zeilen mit Kachel 64 px, sonst 16 px. */
  mitKachel?: boolean;
  /** Navigation als View Transition (Dokument aus der Zeile oeffnen, Designsprache-Tokens 6). */
  uebergang?: boolean;
  /** W51 (D-073): bei sehr grosser Schrift auf schmalem Bildschirm steht der Text unter der Kachel (bausteine.css). */
  textUnterKachel?: boolean;
  /** W73c (D-288 H1): Zustand des Links, etwa die Herkunft fuer den aktiven Tab (Schale `aktiverTab`). */
  zustand?: unknown;
  /** K36 (W82 Web-5b-N1): stabile Kennung der Zeile (`data-testid` am Listeneintrag), etwa Ziel des Rundgangs. */
  kennung?: string;
};

/** Klassen des Inhalts einer Zeile (Link oder Knopf), auch fuer eigene Zeilen wie im Verlauf des Gaists (ChatZeile). */
export const ZEILE_KLASSE =
  "zeile-flex flex w-full min-h-(--pg-hit) items-start gap-3 px-4 py-3 text-left transition-colors duration-(--pg-dur-fast) hover:bg-pg-surface-2 active:bg-pg-surface-2 focus-visible:[outline-offset:-2px]";

/** Zeile einer Gruppe; Trennlinie eingerueckt bis zur Textkante, die letzte Zeile ohne Linie
 *  (per CSS `last:`). Die ganze Zeile ist die Trefferflaeche. */
export function Zeile({ children, vorne, hinten, zu, onClick, label, chevron, mitKachel, uebergang, textUnterKachel, zustand, kennung }: ZeilenProps) {
  const inhalt = (
    <>
      {vorne}
      <span className="zeile-text flex min-w-0 flex-1 flex-col justify-center">{children}</span>
      {hinten && <span className="zeile-hinten flex shrink-0 justify-end">{hinten}</span>}
      {chevron && <ChevronRight size={16} strokeWidth={2} className="zeile-chevron shrink-0 self-center text-pg-text-3" aria-hidden />}
    </>
  );
  const klasse = ZEILE_KLASSE;
  return (
    <li className={`zeile relative ${mitKachel ? "zeile-kachel" : ""} ${textUnterKachel ? "zeile-text-unter-kachel" : ""}`} data-testid={kennung}>
      {zu ? (
        <Link to={zu} state={zustand} className={klasse} aria-label={label} viewTransition={uebergang}>
          {inhalt}
        </Link>
      ) : onClick ? (
        <button type="button" onClick={onClick} className={klasse} aria-label={label}>
          {inhalt}
        </button>
      ) : (
        <div className={klasse}>{inhalt}</div>
      )}
    </li>
  );
}

/** Schluessel-Wert-Zeile (Dokumentkopf: „Person  Anna“). */
export function Eigenschaft(props: { name: string; children: ReactNode }) {
  return (
    <li className="zeile relative flex min-h-(--pg-hit) flex-wrap items-baseline gap-x-4 gap-y-1 px-4 py-3">
      <span className="t-body w-28 shrink-0 text-pg-text-2">{props.name}</span>
      <span className="fliesstext t-body break-words">{props.children}</span>
    </li>
  );
}
